import React from 'react';
/* [CF 2026-08-17] Option labels are built here, outside any component, so
   there is no useTranslation hook. The i18n instance resolves the same keys
   the owner forms already use - vehicle types and quick-service names are
   fixed lists with Arabic in the language file, they just were not being
   looked up on the public filter. */
import i18nInstance from '../../i18n';
import { useTranslation } from 'react-i18next';

/**
 * [CF 2026-08-02] Config-driven filter panel for the non-car categories.
 *
 * One component covers every service category because the fields are declared
 * per page via `fields`: any of 'service' | 'brand' | 'gender' | 'price' |
 * 'vehicleType' | 'openNow'. Each category shows only what its listing rows can
 * actually answer — a control that cannot filter anything is worse than no
 * control, so nothing here is rendered "for consistency".
 *
 * All matching happens in the browser: these listings already return packages,
 * services, brands and the opening calendar, so no API change is required.
 */

export const EMPTY_SERVICE_FILTER = {
  service: '',
  brandId: '',
  gender: '',
  vehicleType: '',
  priceMin: '',
  priceMax: '',
  openNow: false,
};

export function isServiceFilterActive(f) {
  if (!f) return false;
  return (
    f.openNow === true ||
    ['service', 'brandId', 'gender', 'vehicleType', 'priceMin', 'priceMax'].some(
      (k) => String(f[k] ?? '') !== ''
    )
  );
}

/** `calendar` is stored as a JSON string: { MONDAY: {status, openTime, endTime} }. */
export function isOpenNow(calendar) {
  const cal = typeof calendar === 'string' ? safeParse(calendar) : calendar;
  if (!cal) return false;
  const now = new Date();
  const day = now.toLocaleString('en-us', { weekday: 'long' }).toUpperCase();
  const today = cal[day];
  if (!today) return false;
  const { status, openTime, endTime } = today;
  // Vendors save the status in either language — the detail pages accept both.
  if (status !== 'OPEN' && status !== 'مفتوح') return false;
  const open = toMinutes(openTime);
  const close = toMinutes(endTime);
  if (open === null || close === null) return false;
  const cur = now.getHours() * 60 + now.getMinutes();
  return cur >= open && cur <= close;
}

function toMinutes(s) {
  if (!s || typeof s !== 'string') return null;
  const [time, period] = s.split(' ');
  let [h, m] = String(time).split(':').map(Number);
  if (!isFinite(h) || !isFinite(m)) return null;
  if (period === 'PM' && h < 12) h += 12;
  if (period === 'AM' && h === 12) h -= 12;
  return h * 60 + m;
}

function safeParse(s) {
  try {
    return JSON.parse(s);
  } catch (e) {
    return null;
  }
}

const servicesOf = (row) => row?.services || row?.quickServices || [];

/* Garage/AC store the name directly on the row as `service`; Quick Services
   nests it under the master record as `quickService.quickService`. */
const serviceNameOf = (s) => s?.service || s?.quickService?.quickService || '';

/* ...and the brand list is `brands` for garages, `carBrands` for quick. */
const serviceBrandsOf = (s) => s?.brands || s?.carBrands || [];

const brandsOf = (row) => {
  const nested = servicesOf(row)
    .flatMap((s) => serviceBrandsOf(s))
    .map((b) => b?.brand || b)
    .filter((b) => b && b.brandId);
  if (nested.length) return nested;
  // Taxi and Towing store a single brand id on the row, with the brand's own
  // name alongside it in `carDetails` — so the dropdown can be labelled without
  // fetching the 97-row brand master.
  /* [CF 2026-08-17] Taxi and Towing keep a single brand id on the row and the
     brand's own record under `carDetails`. Only `name` was picked up here, so
     the public brand filter on those two pages listed AUDI / BMW / LAND ROVER
     in English however the site was set. The API now returns `nameAr`
     alongside it, so pass it through and let the option list choose. */
  if (row?.car)
    return [
      {
        brandId: row.car,
        name: row?.carDetails?.name,
        nameAr: row?.carDetails?.nameAr,
      },
    ];
  return [];
};

/** Cheapest package a vendor offers — what a price range should test against. */
const priceOf = (row) => {
  const prices = (row?.packages || [])
    .map((p) => parseFloat(p?.price))
    .filter((n) => isFinite(n));
  return prices.length ? Math.min(...prices) : null;
};

export function serviceMatchesFilter(row, f) {
  if (!isServiceFilterActive(f)) return true;

  if (f.service && !servicesOf(row).some((s) => serviceNameOf(s) === f.service))
    return false;

  if (f.brandId && !brandsOf(row).some((b) => b.brandId === f.brandId)) return false;

  if (f.gender && String(row?.gender || '') !== f.gender) return false;

  if (f.vehicleType && String(row?.vehicleType || '') !== f.vehicleType) return false;

  if (f.priceMin !== '' || f.priceMax !== '') {
    const p = priceOf(row);
    if (p === null) return false; // no packages priced → can't satisfy a range
    if (f.priceMin !== '' && p < parseFloat(f.priceMin)) return false;
    if (f.priceMax !== '' && p > parseFloat(f.priceMax)) return false;
  }

  if (f.openNow && !isOpenNow(row?.calendar)) return false;

  return true;
}

/** Options built from the rows on screen, so we never offer an empty result. */
export function optionsFromRows(rows, pick, isRTL) {
  const map = new Map();
  (rows || []).forEach((row) => {
    (pick(row) || []).forEach((o) => {
      if (o && o.value && !map.has(o.value)) {
        map.set(o.value, (isRTL ? o.ar || o.en : o.en || o.ar) || o.value);
      }
    });
  });
  return [...map.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

export function serviceOptionsFromRows(rows, isRTL) {
  return optionsFromRows(
    rows,
    (row) =>
      servicesOf(row).map((s) => ({
        value: serviceNameOf(s),
        en: serviceNameOf(s),
        ar: i18nInstance.t(serviceNameOf(s)),
      })),
    isRTL
  );
}

export function vehicleTypeOptionsFromRows(rows, isRTL) {
  return optionsFromRows(
    rows,
    (row) =>
      row?.vehicleType
        ? [
            {
              value: row.vehicleType,
              en: row.vehicleType,
              ar: i18nInstance.t(row.vehicleType),
            },
          ]
        : [],
    isRTL
  );
}

export function brandOptionsFromRows(rows, isRTL) {
  return optionsFromRows(
    rows,
    (row) => brandsOf(row).map((b) => ({ value: b.brandId, en: b.name, ar: b.nameAr })),
    isRTL
  );
}

const fieldStyle = {
  width: '100%',
  minWidth: 0,
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  padding: '9px 12px',
  border: '1px solid #c9ced4',
  borderRadius: '10px',
  background: '#fff',
  font: 'inherit',
};

const labelStyle = {
  display: 'block',
  fontSize: '.72rem',
  letterSpacing: '.03em',
  textTransform: 'uppercase',
  color: '#8a9099',
  marginBottom: '4px',
  fontWeight: 600,
};

/* [CF 2026-08-02] Declared at MODULE level, not inside the component. A
   component defined inside render is a NEW type on every render, so React
   unmounts and remounts the whole field — which drops focus after each
   keystroke in the number inputs and leaves stale DOM nodes behind. */
function Dropdown({ label, selected, onSelect, options, anyLabel }) {
  return (
    <label style={{ display: 'block', minWidth: 0 }}>
      <span style={labelStyle}>{label}</span>
      <select value={selected} onChange={onSelect} style={fieldStyle}>
        <option value="">{anyLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function ServiceFilterPanel({
  value,
  onChange,
  onApply,
  fields = ['openNow'],
  serviceOptions = [],
  brandOptions = [],
  vehicleTypeOptions = [],
  genderOptions = [],
  priceUnit = 'KD',
}) {
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || 'en').toLowerCase().startsWith('ar');
  const has = (f) => fields.includes(f);

  const set = (key) => (e) =>
    onChange({
      ...value,
      [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    });

  const dd = (label, k, options, anyLabel) => (
    <Dropdown
      label={label}
      selected={value[k]}
      onSelect={set(k)}
      options={options}
      anyLabel={anyLabel}
    />
  );

  return (
    <div
      className="cav-filter-panel cav-car-filter"
      style={{ textAlign: isRTL ? 'right' : 'left' }}
    >
      <div className="cav-car-filter-grid">
        {has('service') && dd(t('Service Type'), 'service', serviceOptions, t('All Services'))}
        {has('brand') && dd(t('Car Brand'), 'brandId', brandOptions, t('All Brands'))}
        {has('vehicleType') && dd(t('Vehicle Type'), 'vehicleType', vehicleTypeOptions, t('All Vehicle Types'))}
        {has('gender') && dd(t('Gender'), 'gender', genderOptions, t('All'))}
        {has('price') && (
          <div style={{ minWidth: 0 }}>
            <span style={labelStyle}>{`${t('Price')} (${priceUnit})`}</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                placeholder={t('Min')}
                value={value.priceMin}
                onChange={set('priceMin')}
                style={fieldStyle}
              />
              <input
                type="number"
                min="0"
                inputMode="numeric"
                placeholder={t('Max')}
                value={value.priceMax}
                onChange={set('priceMax')}
                style={fieldStyle}
              />
            </div>
          </div>
        )}
        {has('openNow') && (
          <div style={{ minWidth: 0, display: 'flex', alignItems: 'flex-end' }}>
            <label className="cav-filter-toggle">
              <input type="checkbox" checked={value.openNow} onChange={set('openNow')} />
              <span>{t('Open Now')}</span>
            </label>
          </div>
        )}
      </div>

      <div className="cav-car-filter-actions">
        <button
          type="button"
          className="cav-filter-reset"
          onClick={() => onChange({ ...EMPTY_SERVICE_FILTER })}
          disabled={!isServiceFilterActive(value)}
        >
          {t('Reset')}
        </button>
        <button type="button" className="cav-filter-apply" onClick={onApply}>
          {t('Apply')}
        </button>
      </div>
    </div>
  );
}
